import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../components/organisms/Header';
import Icon from '../components/atoms/Icon';

const LiveInterviewWelcomePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [candidateName, setCandidateName] = useState('');
  const [jobTitle, setJobTitle] = useState('');
  const [duration, setDuration] = useState(5);

  useEffect(() => {
    const name = location.state?.candidateName || localStorage.getItem('dutlaunch_candidate_name') || 'Candidate';
    const title = location.state?.jobTitle || 'Position';
    const dur = location.state?.duration || 5;

    setCandidateName(name);
    setJobTitle(title);
    setDuration(dur);
  }, [location.state]);

  const handleContinue = () => {
    navigate('/live-interview/device-check', {
      state: { ...location.state }
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header variant="default" />

      <main className="flex-1 flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-2xl">
          <div className="bg-white border border-gray-200 rounded-2xl p-10">
            {/* Badge */}
            <div className="flex items-center gap-2 mb-8">
              <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse"></div>
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wide">Live AI Interview</span>
            </div>

            {/* Header */}
            <div className="mb-8">
              <div className="w-12 h-12 bg-[#0a1929] rounded-xl flex items-center justify-center mb-6">
                <Icon name="record_voice_over" size={24} className="text-white" />
              </div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">Welcome to Your Live Interview</h1>
              <p className="text-gray-500 text-sm">Hi {candidateName},</p>
            </div>

            <p className="text-gray-600 text-sm leading-relaxed mb-6">
              You're about to begin a live AI-powered interview for the <span className="font-medium text-gray-900">{jobTitle}</span> position.
              This will be a real-time conversation with our AI interviewer.
            </p>

            {/* Duration badge */}
            <div className="inline-flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg mb-8">
              <Icon name="schedule" size={16} className="text-[#0a1929]" />
              <span className="text-sm font-medium text-gray-700">Duration: {duration} minutes</span>
            </div>

            {/* What to expect */}
            <div className="mb-8">
              <h3 className="text-xs font-medium text-gray-400 uppercase tracking-wide mb-4">What to Expect</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {[
                  { icon: 'mic', title: 'Voice Conversation', desc: 'Speak naturally with the AI interviewer' },
                  { icon: 'psychology', title: 'AI Evaluation', desc: 'Responses analyzed for feedback' },
                  { icon: 'videocam', title: 'Video Monitoring', desc: 'Camera monitors for conduct' },
                  { icon: 'timer', title: 'Timed Session', desc: `Ends automatically after ${duration} min` },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 border border-gray-100 rounded-xl">
                    <div className="w-8 h-8 bg-[#0a1929] rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={item.icon} size={16} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Important notes */}
            <div className="bg-amber-50 border border-amber-100 rounded-xl p-4 mb-8">
              <div className="flex items-start gap-2.5">
                <Icon name="info" size={16} className="text-amber-600 mt-0.5 flex-shrink-0" />
                <div className="text-xs text-gray-600 space-y-1">
                  <p className="font-medium text-gray-900">Important</p>
                  <p>Quiet, well-lit environment. Keep face visible. Speak clearly.</p>
                </div>
              </div>
            </div>

            <button
              onClick={handleContinue}
              className="w-full py-3.5 bg-[#0a1929] hover:bg-[#0d2137] text-white text-sm font-semibold rounded-xl transition-colors flex items-center justify-center gap-2"
            >
              Continue to Setup
              <Icon name="arrow_forward" size={18} className="text-white" />
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LiveInterviewWelcomePage;
